import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ToolCard from './components/ToolCard';
import MusicPlayer from './components/MusicPlayer';

export default function Tools() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedMusicContext, setSelectedMusicContext] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [selectedMusic, setSelectedMusic] = useState<typeof sampleMusic[0] | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const tools = [
    {
      id: 1,
      name: 'Church Bulletin Templates',
      description: 'Ready-to-use bulletin templates for Sunday worship, special programs, and church events. Editable in Microsoft Word and Google Docs.',
      icon: 'ri-newspaper-line',
      color: 'from-emerald-500 to-emerald-600',
      features: ['Sabbath Worship Bulletins', 'Special Event Programs', 'Wedding & Funeral Orders', 'Fully Editable'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      image: 'https://public.readdy.ai/ai/img_res/7fbec5800bcacdc0db63f30e932c4ca3.jpg',
    },
    {
      id: 2,
      name: 'PowerPoint Worship Slides',
      description: 'Beautiful PowerPoint templates for song lyrics, Bible verses, sermon points, and announcements designed for Nigerian churches.',
      icon: 'ri-slideshow-line',
      color: 'from-blue-500 to-blue-600',
      features: ['Song Lyric Slides', 'Bible Verse Templates', 'Sermon Backgrounds', 'Announcement Slides'],
      status: 'Available',
      category: 'Templates',
      type: 'PPTX',
      image: 'https://readdy.ai/api/search-image?query=Modern%20church%20PowerPoint%20presentation%20slide%20template%20with%20elegant%20worship%20background%20showing%20song%20lyrics%20and%20Bible%20verses%20in%20clean%20readable%20typography%20suitable%20for%20projection&width=600&height=400&seq=tool-ppt-001&orientation=landscape',
    },
    {
      id: 3,
      name: 'Google Drive Worship Library',
      description: 'Organized Google Drive folders with hymns, songs, sermon outlines, and ministry resources accessible from anywhere.',
      icon: 'ri-google-line',
      color: 'from-yellow-500 to-amber-600',
      features: ['Cloud Storage', 'Easy Sharing', 'Mobile Access', 'Organized Folders'],
      status: 'Available',
      category: 'Digital Resources',
      type: 'Software',
      image: 'https://readdy.ai/api/search-image?query=Google%20Drive%20interface%20showing%20organized%20church%20worship%20folders%20with%20hymns%20songs%20sermons%20and%20ministry%20resources%20in%20clean%20folder%20structure%20on%20computer%20screen&width=600&height=400&seq=tool-drive-001&orientation=landscape',
    },
    {
      id: 4,
      name: 'Nigerian Church Graphics Pack',
      description: 'High-quality images, backgrounds, and graphics featuring Nigerian cultural elements for worship presentations and bulletins.',
      icon: 'ri-image-line',
      color: 'from-purple-500 to-purple-600',
      features: ['Cultural Backgrounds', 'Worship Images', 'Event Graphics', 'Social Media Templates'],
      status: 'Available',
      category: 'Graphics',
      type: 'PSD',
      image: 'https://readdy.ai/api/search-image?query=Collection%20of%20beautiful%20Nigerian%20church%20worship%20graphics%20and%20backgrounds%20featuring%20African%20cultural%20elements%20vibrant%20colors%20and%20Christian%20symbols%20suitable%20for%20presentations%20and%20bulletins&width=600&height=400&seq=tool-graphics-001&orientation=landscape',
    },
    {
      id: 5,
      name: 'Hymnal & Song Lyrics Database',
      description: 'Comprehensive collection of SDA hymns and Nigerian gospel songs with lyrics, chord charts, and audio recordings.',
      icon: 'ri-music-2-line',
      color: 'from-teal-500 to-teal-600',
      features: ['SDA Hymnal', 'Nigerian Gospel Songs', 'Chord Charts', 'Audio Files'],
      status: 'Available',
      category: 'Music',
      type: 'PDF',
      image: 'https://readdy.ai/api/search-image?query=Digital%20hymnal%20database%20interface%20showing%20song%20lyrics%20chord%20charts%20and%20music%20notation%20for%20Nigerian%20church%20worship%20songs%20in%20organized%20searchable%20format&width=600&height=400&seq=tool-hymnal-001&orientation=landscape',
    },
    {
      id: 6,
      name: 'Sermon Outline Templates',
      description: 'Pre-formatted sermon outline templates with space for scripture, main points, illustrations, and application.',
      icon: 'ri-file-text-line',
      color: 'from-rose-500 to-rose-600',
      features: ['Structured Outlines', 'Scripture References', 'Illustration Sections', 'Printable Format'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      link: '#'
    },
    {
      id: 7,
      name: 'Church Event Flyer Templates',
      description: 'Eye-catching flyer designs for revivals, concerts, seminars, and special church events with Nigerian aesthetics.',
      icon: 'ri-layout-line',
      color: 'from-orange-500 to-orange-600',
      features: ['Event Flyers', 'Social Media Posts', 'Banners', 'Editable Designs'],
      status: 'Available',
      category: 'Graphics',
      type: 'PSD',
      image: 'https://readdy.ai/api/search-image?query=Vibrant%20Nigerian%20church%20event%20flyer%20design%20with%20bold%20colors%20African%20patterns%20and%20modern%20typography%20for%20revival%20concert%20or%20seminar%20promotion&width=600&height=400&seq=tool-flyer-001&orientation=landscape',
    },
    {
      id: 8,
      name: 'Order of Service Templates',
      description: 'Professional order of service templates for regular worship, communion services, baptisms, and special occasions.',
      icon: 'ri-list-ordered',
      color: 'from-indigo-500 to-indigo-600',
      features: ['Worship Service Order', 'Communion Service', 'Baptism Order', 'Special Programs'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      link: '#'
    },
    {
      id: 9,
      name: 'Bible Verse Graphics',
      description: 'Beautiful scripture graphics with Nigerian-themed backgrounds perfect for social media, bulletins, and presentations.',
      icon: 'ri-book-open-line',
      color: 'from-green-500 to-green-600',
      features: ['Daily Verse Graphics', 'Themed Backgrounds', 'Multiple Formats', 'Social Media Ready'],
      status: 'Available',
      category: 'Graphics',
      type: 'Canva',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20Bible%20verse%20graphic%20design%20with%20Nigerian%20cultural%20background%20elements%20showing%20scripture%20text%20in%20elegant%20typography%20suitable%20for%20social%20media%20and%20church%20presentations&width=600&height=400&seq=tool-verse-001&orientation=landscape',
    },
    {
      id: 10,
      name: 'Church Calendar Templates',
      description: 'Annual and monthly church calendar templates to plan and communicate events, programs, and special services.',
      icon: 'ri-calendar-line',
      color: 'from-cyan-500 to-cyan-600',
      features: ['Annual Calendar', 'Monthly Planner', 'Event Scheduler', 'Printable Format'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      link: '#'
    },
    {
      id: 11,
      name: 'Offering Envelope Designs',
      description: 'Professional offering envelope templates for tithes, offerings, and special contributions with tracking fields.',
      icon: 'ri-mail-line',
      color: 'from-amber-500 to-amber-600',
      features: ['Tithe Envelopes', 'Offering Envelopes', 'Special Contributions', 'Tracking Fields'],
      status: 'Available',
      category: 'Templates',
      type: 'CorelDRAW',
      link: '#'
    },
    {
      id: 12,
      name: 'Church Letterhead Templates',
      description: 'Official church letterhead designs for correspondence, certificates, and official documents.',
      icon: 'ri-file-paper-line',
      color: 'from-slate-500 to-slate-600',
      features: ['Official Letterhead', 'Certificate Templates', 'Document Headers', 'Professional Design'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      link: '#'
    },
    {
      id: 13,
      name: 'Sabbath School Materials',
      description: 'Quarterly lesson guides, discussion questions, and teaching aids for adult and youth Sabbath School classes.',
      icon: 'ri-book-2-line',
      color: 'from-violet-500 to-violet-600',
      features: ['Lesson Guides', 'Discussion Questions', 'Teaching Aids', 'Quarterly Materials'],
      status: 'Available',
      category: 'Digital Resources',
      type: 'PDF',
      link: '#'
    },
    {
      id: 14,
      name: 'Prayer Request Forms',
      description: 'Simple prayer request forms for collecting and organizing prayer needs from church members.',
      icon: 'ri-hand-heart-line',
      color: 'from-pink-500 to-pink-600',
      features: ['Request Forms', 'Prayer Lists', 'Follow-up Tracking', 'Printable Format'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      link: '#'
    },
    {
      id: 15,
      name: 'Visitor Welcome Cards',
      description: 'Friendly visitor cards to collect contact information and connect with first-time guests.',
      icon: 'ri-user-smile-line',
      color: 'from-emerald-500 to-emerald-600',
      features: ['Welcome Cards', 'Contact Forms', 'Follow-up Tools', 'Professional Design'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      link: '#'
    },
    {
      id: 16,
      name: 'Church Announcement Slides',
      description: 'Pre-designed announcement slide templates for upcoming events, birthdays, and church news.',
      icon: 'ri-notification-line',
      color: 'from-blue-500 to-blue-600',
      features: ['Event Announcements', 'Birthday Slides', 'News Updates', 'Easy Customization'],
      status: 'Available',
      category: 'Templates',
      type: 'PPTX',
      link: '#'
    },
    {
      id: 17,
      name: 'Ministry Report Templates',
      description: 'Structured templates for ministry leaders to report activities, attendance, and financial summaries.',
      icon: 'ri-bar-chart-line',
      color: 'from-teal-500 to-teal-600',
      features: ['Activity Reports', 'Attendance Tracking', 'Financial Summaries', 'Professional Format'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      link: '#'
    },
    {
      id: 18,
      name: 'Church Manuals',
      description: 'Sample church constitution and bylaws templates adapted for Nigerian SDA churches.',
      icon: 'ri-article-line',
      color: 'from-gray-500 to-gray-600',
      features: ['Constitution Template', 'Bylaws Sample', 'Governance Guidelines', 'Editable Format'],
      status: 'Available',
      category: 'Digital Resources',
      type: 'PDF',
      link: '#'
    },
    {
      id: 19,
      name: 'Baptismal Certificate Templates',
      description: 'Beautiful baptismal certificate designs to commemorate this special occasion in a member\'s life.',
      icon: 'ri-award-line',
      color: 'from-blue-500 to-cyan-600',
      features: ['Certificate Designs', 'Customizable Fields', 'Professional Quality', 'Printable Format'],
      status: 'Available',
      category: 'Templates',
      type: 'CorelDRAW',
      link: '#'
    },
    {
      id: 20,
      name: 'Church Budget Templates',
      description: 'Comprehensive budget planning templates for annual church finances and ministry allocations.',
      icon: 'ri-money-dollar-circle-line',
      color: 'from-green-500 to-emerald-600',
      features: ['Annual Budget', 'Ministry Allocations', 'Expense Tracking', 'Financial Planning'],
      status: 'Available',
      category: 'Templates',
      type: 'PDF',
      link: '#'
    },
    {
      id: 21,
      name: 'Social Media Graphics Pack',
      description: 'Ready-to-use Canva templates for Instagram, Facebook, and WhatsApp posts with Nigerian church themes.',
      icon: 'ri-share-line',
      color: 'from-pink-500 to-rose-600',
      features: ['Instagram Posts', 'Facebook Banners', 'WhatsApp Status', 'Story Templates'],
      status: 'Available',
      category: 'Graphics',
      type: 'Canva',
      link: '#'
    },
    {
      id: 22,
      name: 'Church Branding Kit',
      description: 'Complete branding package with logo templates, color schemes, and design guidelines in PSD and CorelDRAW formats.',
      icon: 'ri-palette-line',
      color: 'from-indigo-500 to-purple-600',
      features: ['Logo Templates', 'Color Palettes', 'Brand Guidelines', 'Multiple Formats'],
      status: 'Available',
      category: 'Graphics',
      type: 'PSD',
      link: '#'
    },
    {
      id: 23,
      name: 'Worship Presentation Bundle',
      description: 'Complete PowerPoint bundle with animated backgrounds, transitions, and countdown timers for worship services.',
      icon: 'ri-presentation-line',
      color: 'from-emerald-500 to-teal-600',
      features: ['Animated Backgrounds', 'Countdown Timers', 'Transition Effects', 'Song Templates'],
      status: 'Available',
      category: 'Templates',
      type: 'PPTX',
      link: '#'
    },
    {
      id: 24,
      name: 'Church Banner Designs',
      description: 'Professional banner designs for church events, conferences, and outdoor advertising in CorelDRAW and PSD formats.',
      icon: 'ri-flag-line',
      color: 'from-orange-500 to-red-600',
      features: ['Event Banners', 'Conference Designs', 'Outdoor Ads', 'Print-Ready Files'],
      status: 'Available',
      category: 'Graphics',
      type: 'CorelDRAW',
      link: '#'
    }
  ];

  const sampleMusic = [
    {
      id: 1,
      title: 'Joyful Welcome Hymn',
      context: 'Welcome Songs',
      description: 'Warm welcoming hymn to greet congregation as they arrive',
      duration: '3:45',
      artist: 'Lagos Central Choir',
      language: 'English/Yoruba',
      icon: 'ri-door-open-line',
      color: 'from-blue-500 to-indigo-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Warm%20welcoming%20church%20scene%20with%20congregation%20arriving%20and%20greeting%20each%20other%20with%20smiles%20and%20handshakes%20in%20bright%20morning%20light%20showing%20hospitality%20and%20fellowship%20simple%20clean%20background&width=600&height=400&seq=music-welcome-001&orientation=landscape'
    },
    {
      id: 2,
      title: 'Opening Praise Medley',
      context: 'Opening Songs',
      description: 'Uplifting opening songs to begin worship service',
      duration: '4:20',
      artist: 'Hope Evangelism Network',
      language: 'English/Igbo',
      icon: 'ri-music-2-line',
      color: 'from-emerald-500 to-teal-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Energetic%20church%20opening%20worship%20with%20congregation%20standing%20and%20singing%20with%20raised%20hands%20bright%20uplifting%20atmosphere%20showing%20joy%20and%20praise%20simple%20background&width=600&height=400&seq=music-opening-001&orientation=landscape'
    },
    {
      id: 3,
      title: 'Thanksgiving Medley',
      context: 'Thanksgiving / Offertory / Praise',
      description: 'Joyful thanksgiving and praise songs for offering time',
      duration: '5:12',
      artist: 'Adventist Hymnal Collection',
      language: 'English/Yoruba',
      icon: 'ri-heart-3-line',
      color: 'from-amber-500 to-orange-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Joyful%20Nigerian%20church%20thanksgiving%20worship%20with%20people%20giving%20offerings%20with%20gratitude%20and%20celebration%20warm%20golden%20lighting%20showing%20generosity%20simple%20background&width=600&height=400&seq=music-thanksgiving-002&orientation=landscape'
    },
    {
      id: 4,
      title: 'Prayer Response',
      context: 'Prayer Songs',
      description: 'Gentle background music for prayer moments',
      duration: '4:30',
      artist: 'Church Orchestra',
      language: 'Instrumental',
      icon: 'ri-hands-pray-line',
      color: 'from-indigo-500 to-purple-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Peaceful%20prayer%20moment%20in%20church%20with%20people%20in%20deep%20prayer%20and%20meditation%20soft%20gentle%20lighting%20showing%20spiritual%20connection%20simple%20tranquil%20background&width=600&height=400&seq=music-prayer-002&orientation=landscape'
    },
    {
      id: 5,
      title: 'Opening Invocation Hymn',
      context: 'Invocations',
      description: 'Reverent invocation hymn calling on God\'s presence',
      duration: '3:45',
      artist: 'Youth Alive Movement',
      language: 'English',
      icon: 'ri-hand-heart-line',
      color: 'from-blue-600 to-cyan-700',
      imageUrl: 'https://readdy.ai/api/search-image?query=Reverent%20church%20invocation%20scene%20with%20soft%20morning%20light%20streaming%20through%20windows%20showing%20prayer%20and%20calling%20on%20divine%20presence%20simple%20elegant%20background&width=600&height=400&seq=music-invocation-002&orientation=landscape'
    },
    {
      id: 6,
      title: 'Call to Worship',
      context: 'Call-to Worship Songs',
      description: 'Majestic call inviting congregation to worship',
      duration: '3:15',
      artist: 'Lagos Central Choir',
      language: 'English/Hausa',
      icon: 'ri-megaphone-line',
      color: 'from-purple-500 to-pink-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Majestic%20church%20call%20to%20worship%20scene%20with%20congregation%20responding%20to%20invitation%20bright%20inspiring%20atmosphere%20showing%20unity%20and%20reverence%20simple%20background&width=600&height=400&seq=music-callworship-001&orientation=landscape'
    },
    {
      id: 7,
      title: 'Stillness Meditation',
      context: 'Stillness Songs',
      description: 'Quiet reflective music for moments of stillness',
      duration: '4:50',
      artist: 'Church Orchestra',
      language: 'Instrumental',
      icon: 'ri-moon-line',
      color: 'from-slate-500 to-gray-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Peaceful%20church%20stillness%20moment%20with%20quiet%20meditation%20and%20reflection%20soft%20dim%20lighting%20showing%20serenity%20and%20contemplation%20simple%20serene%20background&width=600&height=400&seq=music-stillness-001&orientation=landscape'
    },
    {
      id: 8,
      title: 'Consecration Hymn',
      context: 'Consecration Songs',
      description: 'Solemn hymn of dedication and commitment',
      duration: '4:05',
      artist: 'Hope Evangelism Network',
      language: 'English/Yoruba',
      icon: 'ri-hand-heart-fill',
      color: 'from-rose-500 to-red-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Solemn%20church%20consecration%20moment%20with%20people%20in%20dedication%20and%20commitment%20soft%20reverent%20lighting%20showing%20surrender%20and%20devotion%20simple%20background&width=600&height=400&seq=music-consecration-001&orientation=landscape'
    },
    {
      id: 9,
      title: 'Benediction Hymn',
      context: 'Closing / Benediction Songs',
      description: 'Peaceful closing hymn for service benediction',
      duration: '3:28',
      artist: 'Adventist Hymnal Collection',
      language: 'English/Hausa',
      icon: 'ri-hand-heart-line',
      color: 'from-cyan-500 to-blue-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Peaceful%20church%20benediction%20scene%20with%20congregation%20in%20prayer%20and%20blessing%20moment%20soft%20evening%20light%20showing%20closure%20and%20peace%20simple%20serene%20background&width=600&height=400&seq=music-benediction-002&orientation=landscape'
    },
    {
      id: 10,
      title: 'Birthday Celebration',
      context: 'Birthdays',
      description: 'Joyful birthday celebration songs for church members',
      duration: '2:40',
      artist: 'Youth Alive Movement',
      language: 'English/Pidgin',
      icon: 'ri-cake-3-line',
      color: 'from-pink-500 to-rose-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Joyful%20church%20birthday%20celebration%20with%20people%20singing%20and%20celebrating%20with%20cake%20and%20balloons%20bright%20festive%20atmosphere%20showing%20joy%20and%20community%20simple%20background&width=600&height=400&seq=music-birthday-001&orientation=landscape'
    },
    {
      id: 11,
      title: 'National Anthem Medley',
      context: 'National Events',
      description: 'Patriotic songs for national celebrations and events',
      duration: '4:15',
      artist: 'Lagos Central Choir',
      language: 'English',
      icon: 'ri-flag-line',
      color: 'from-green-600 to-emerald-700',
      imageUrl: 'https://readdy.ai/api/search-image?query=Patriotic%20church%20national%20event%20celebration%20with%20Nigerian%20flag%20and%20people%20singing%20national%20songs%20proud%20dignified%20atmosphere%20showing%20unity%20and%20patriotism%20simple%20background&width=600&height=400&seq=music-national-001&orientation=landscape'
    },
    {
      id: 12,
      title: 'Child Dedication Hymn',
      context: 'Dedications',
      description: 'Tender hymn for baby and child dedication services',
      duration: '3:50',
      artist: 'Church Orchestra',
      language: 'English/Yoruba',
      icon: 'ri-parent-line',
      color: 'from-blue-400 to-cyan-500',
      imageUrl: 'https://readdy.ai/api/search-image?query=Tender%20church%20child%20dedication%20ceremony%20with%20parents%20and%20baby%20at%20altar%20soft%20warm%20lighting%20showing%20blessing%20and%20commitment%20simple%20peaceful%20background&width=600&height=400&seq=music-dedication-001&orientation=landscape'
    },
    {
      id: 13,
      title: 'Youth Praise Explosion',
      context: 'Youth / Children Focus',
      description: 'Energetic contemporary songs for youth and children',
      duration: '5:30',
      artist: 'Youth Alive Movement',
      language: 'English/Pidgin',
      icon: 'ri-user-star-line',
      color: 'from-orange-500 to-amber-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Energetic%20youth%20church%20worship%20with%20young%20people%20dancing%20and%20celebrating%20with%20modern%20instruments%20vibrant%20dynamic%20atmosphere%20showing%20enthusiasm%20simple%20background&width=600&height=400&seq=music-youth-001&orientation=landscape'
    },
    {
      id: 14,
      title: 'AMO Celebration Songs',
      context: 'AMO',
      description: 'Special songs for Adventist Men\'s Organization events',
      duration: '4:45',
      artist: 'Hope Evangelism Network',
      language: 'English/Hausa',
      icon: 'ri-user-line',
      color: 'from-indigo-600 to-purple-700',
      imageUrl: 'https://readdy.ai/api/search-image?query=Church%20men%20ministry%20gathering%20with%20men%20in%20worship%20and%20fellowship%20strong%20dignified%20atmosphere%20showing%20brotherhood%20and%20faith%20simple%20background&width=600&height=400&seq=music-amo-001&orientation=landscape'
    },
    {
      id: 15,
      title: 'AWM Worship Medley',
      context: 'AWM',
      description: 'Inspiring songs for Adventist Women\'s Ministries',
      duration: '5:00',
      artist: 'Lagos Central Choir',
      language: 'English/Igbo',
      icon: 'ri-user-smile-line',
      color: 'from-pink-600 to-rose-700',
      imageUrl: 'https://readdy.ai/api/search-image?query=Church%20women%20ministry%20worship%20with%20women%20singing%20and%20praising%20together%20warm%20nurturing%20atmosphere%20showing%20sisterhood%20and%20devotion%20simple%20background&width=600&height=400&seq=music-awm-001&orientation=landscape'
    },
    {
      id: 16,
      title: 'Health Week Anthem',
      context: 'Health Week',
      description: 'Uplifting songs promoting health and wellness',
      duration: '3:55',
      artist: 'Adventist Hymnal Collection',
      language: 'English',
      icon: 'ri-heart-pulse-line',
      color: 'from-teal-500 to-cyan-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Church%20health%20week%20celebration%20with%20people%20exercising%20and%20promoting%20wellness%20bright%20healthy%20atmosphere%20showing%20vitality%20and%20stewardship%20simple%20background&width=600&height=400&seq=music-health-001&orientation=landscape'
    },
    {
      id: 17,
      title: 'Fasting & Prayer Hymn',
      context: 'Fasting & Prayer',
      description: 'Solemn songs for fasting and prayer programs',
      duration: '4:20',
      artist: 'Church Orchestra',
      language: 'English/Yoruba',
      icon: 'ri-restaurant-line',
      color: 'from-purple-600 to-indigo-700',
      imageUrl: 'https://readdy.ai/api/search-image?query=Solemn%20church%20fasting%20and%20prayer%20gathering%20with%20people%20in%20deep%20intercession%20soft%20reverent%20lighting%20showing%20spiritual%20intensity%20simple%20background&width=600&height=400&seq=music-fasting-001&orientation=landscape'
    },
    {
      id: 18,
      title: 'Baptismal Celebration',
      context: 'Baptism',
      description: 'Joyful celebration music for baptismal services',
      duration: '3:55',
      artist: 'Hope Evangelism Network',
      language: 'English/Yoruba',
      icon: 'ri-water-flash-line',
      color: 'from-blue-400 to-cyan-500',
      imageUrl: 'https://readdy.ai/api/search-image?query=Joyful%20baptism%20celebration%20in%20church%20with%20water%20baptistry%20and%20people%20celebrating%20new%20life%20bright%20hopeful%20lighting%20showing%20rebirth%20and%20joy%20simple%20clean%20background&width=600&height=400&seq=music-baptism-002&orientation=landscape'
    },
    {
      id: 19,
      title: 'Communion Meditation',
      context: 'Holy Communion',
      description: 'Solemn instrumental music for communion service',
      duration: '4:50',
      artist: 'Church Orchestra',
      language: 'Instrumental',
      icon: 'ri-goblet-line',
      color: 'from-slate-500 to-gray-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Solemn%20communion%20service%20scene%20with%20bread%20and%20wine%20elements%20soft%20reverent%20lighting%20showing%20sacred%20moment%20of%20reflection%20simple%20elegant%20background&width=600&height=400&seq=music-communion-002&orientation=landscape'
    },
    {
      id: 20,
      title: 'Special Request Solo',
      context: 'Solo / Group Requests',
      description: 'Beautiful solo and group performances for special moments',
      duration: '4:10',
      artist: 'Youth Alive Movement',
      language: 'English/Igbo',
      icon: 'ri-mic-line',
      color: 'from-amber-500 to-orange-600',
      imageUrl: 'https://readdy.ai/api/search-image?query=Church%20solo%20performance%20with%20singer%20at%20microphone%20delivering%20special%20music%20emotional%20intimate%20atmosphere%20showing%20ministry%20through%20song%20simple%20background&width=600&height=400&seq=music-solo-001&orientation=landscape'
    }
  ];

  const musicContexts = [
    { label: 'All Music', value: 'All', icon: 'ri-music-2-line' },
    { label: 'Welcome Songs', value: 'Welcome Songs', icon: 'ri-door-open-line' },
    { label: 'Opening Songs', value: 'Opening Songs', icon: 'ri-music-2-line' },
    { label: 'Thanksgiving / Offertory / Praise', value: 'Thanksgiving / Offertory / Praise', icon: 'ri-heart-3-line' },
    { label: 'Prayer Songs', value: 'Prayer Songs', icon: 'ri-hands-pray-line' },
    { label: 'Invocations', value: 'Invocations', icon: 'ri-hand-heart-line' },
    { label: 'Call-to Worship Songs', value: 'Call-to Worship Songs', icon: 'ri-megaphone-line' },
    { label: 'Stillness Songs', value: 'Stillness Songs', icon: 'ri-moon-line' },
    { label: 'Consecration Songs', value: 'Consecration Songs', icon: 'ri-hand-heart-fill' },
    { label: 'Closing / Benediction Songs', value: 'Closing / Benediction Songs', icon: 'ri-hand-heart-line' },
    { label: 'Birthdays', value: 'Birthdays', icon: 'ri-cake-3-line' },
    { label: 'National Events', value: 'National Events', icon: 'ri-flag-line' },
    { label: 'Dedications', value: 'Dedications', icon: 'ri-parent-line' },
    { label: 'Youth / Children Focus', value: 'Youth / Children Focus', icon: 'ri-user-star-line' },
    { label: 'AMO', value: 'AMO', icon: 'ri-user-line' },
    { label: 'AWM', value: 'AWM', icon: 'ri-user-smile-line' },
    { label: 'Health Week', value: 'Health Week', icon: 'ri-heart-pulse-line' },
    { label: 'Fasting & Prayer', value: 'Fasting & Prayer', icon: 'ri-restaurant-line' },
    { label: 'Baptism', value: 'Baptism', icon: 'ri-water-flash-line' },
    { label: 'Holy Communion', value: 'Holy Communion', icon: 'ri-goblet-line' },
    { label: 'Solo / Group Requests', value: 'Solo / Group Requests', icon: 'ri-mic-line' }
  ];

  const categories = [
    { label: 'All Resources', value: 'All', icon: 'ri-apps-line' },
    { label: 'Templates', value: 'Templates', icon: 'ri-file-copy-line' },
    { label: 'Graphics', value: 'Graphics', icon: 'ri-image-line' },
    { label: 'Music', value: 'Music', icon: 'ri-music-2-line' },
    { label: 'Digital Resources', value: 'Digital Resources', icon: 'ri-cloud-line' }
  ];

  const filteredTools = tools.filter(tool => {
    const matchesCategory = selectedCategory === 'All' || tool.category === selectedCategory;
    return matchesCategory;
  });

  const filteredMusic = sampleMusic.filter(music => {
    return selectedMusicContext === 'All' || music.context === selectedMusicContext;
  });

  const getCategoryCount = (category: string) => {
    if (category === 'All') return tools.length;
    return tools.filter(tool => tool.category === category).length;
  };

  const getMusicContextCount = (context: string) => {
    if (context === 'All') return sampleMusic.length;
    return sampleMusic.filter(music => music.context === context).length;
  };

  const handleFileDownload = (fileName: string, fileType: string) => {
    // Create a mock download - in production, this would fetch the actual file
    const link = document.createElement('a');
    link.href = '#'; // In production, this would be the actual file URL
    link.download = fileName;
    
    // Show a brief notification
    const notification = document.createElement('div');
    notification.className = 'fixed bottom-4 right-4 bg-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    notification.innerHTML = `
      <i class="ri-download-line"></i>
      <span>Downloading ${fileName}...</span>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  };

  const handleDownloadAll = (music: typeof sampleMusic[0]) => {
    setSelectedMusic(music);
    setShowEmailModal(true);
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -1000 : 1000;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-tools-line text-emerald-400"></i>
              <span className="text-sm font-medium text-white/90">Worship Aids for Nigerian Churches</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              Worship Tools & Resources
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto">
              Editable templates, graphics, and digital resources designed specifically for Nigerian Seventh-day Adventist churches
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Info Banner */}
        <div className="mb-10 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                Everything You Need for Worship
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Access professionally designed bulletins, PowerPoint slides, graphics, and templates tailored for Nigerian church contexts. 
                All resources are fully editable and ready to use for your worship services, events, and ministry programs.
              </p>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map(category => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center space-x-2 ${
                  selectedCategory === category.value
                    ? 'bg-emerald-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg'
                }`}
              >
                <i className={`${category.icon} text-lg`}></i>
                <span>{category.label}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                  selectedCategory === category.value
                    ? 'bg-white/20 text-white'
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {getCategoryCount(category.value)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-center">
          <p className="text-sm text-gray-600">
            Showing <strong>{filteredTools.length}</strong> of <strong>{tools.length}</strong> tools
            {selectedCategory !== 'All' && (
              <button
                onClick={() => setSelectedCategory('All')}
                className="ml-3 text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer whitespace-nowrap underline"
              >
                Clear Filter
              </button>
            )}
          </p>
        </div>

        {/* Spiritual Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              Spiritual Tools & Resources
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Access powerful tools to enhance your spiritual journey and daily devotion
            </p>
          </div>

          <div className="relative">
            {/* Left Arrow */}
            {canScrollLeft && (
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-14 h-14 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300 cursor-pointer border-2 border-white hover:scale-110 group"
                aria-label="Scroll left"
              >
                <i className="ri-arrow-left-s-line text-3xl text-white group-hover:animate-pulse"></i>
              </button>
            )}

            {/* Right Arrow */}
            {canScrollRight && (
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-14 h-14 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300 cursor-pointer border-2 border-white hover:scale-110 group"
                aria-label="Scroll right"
              >
                <i className="ri-arrow-right-s-line text-3xl text-white group-hover:animate-pulse"></i>
              </button>
            )}

            {/* Scrollable Container */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {tools.map((tool, index) => (
                <div key={index} className="flex-shrink-0 w-80">
                  <ToolCard tool={tool} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sample Music Section - Moved Here */}
        <div className="mt-16 mb-12 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white">
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
              <i className="ri-music-2-line text-white"></i>
              <span className="text-sm font-medium">Sample Worship Music</span>
            </div>
            <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              Nigerian Worship Music Library
            </h2>
            <p className="text-white/90 max-w-3xl mx-auto">
              Listen to sample music for different worship moments - from invocation to benediction, 
              featuring Nigerian languages and cultural expressions
            </p>
          </div>

          {/* Music Context Filter */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 justify-center">
              {musicContexts.map(context => (
                <button
                  key={context.value}
                  onClick={() => setSelectedMusicContext(context.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center space-x-2 ${
                    selectedMusicContext === context.value
                      ? 'bg-white text-emerald-600 shadow-lg scale-105'
                      : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
                  }`}
                >
                  <i className={`${context.icon} text-base`}></i>
                  <span>{context.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    selectedMusicContext === context.value
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-white/20 text-white'
                  }`}>
                    {getMusicContextCount(context.value)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Music Results Count */}
          <div className="mb-6 text-center">
            <p className="text-sm text-white/80">
              Showing <strong className="text-white">{filteredMusic.length}</strong> tracks
              {selectedMusicContext !== 'All' && (
                <button
                  onClick={() => setSelectedMusicContext('All')}
                  className="ml-3 text-white hover:text-white/80 font-medium cursor-pointer whitespace-nowrap underline"
                >
                  Clear Filter
                </button>
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMusic.map(music => (
              <MusicPlayer 
                key={music.id} 
                title={music.title}
                context={music.context}
                duration={music.duration}
                imageUrl={music.imageUrl}
              />
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-white/80 text-sm mb-4">
              <i className="ri-information-line mr-2"></i>
              These are sample tracks. Full music library available for download below
            </p>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-16 bg-white rounded-xl shadow-md p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-question-line text-3xl text-emerald-600"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
            Need Help Getting Started?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Our worship resources are designed to be simple and easy to use. Most templates work with Microsoft Word, PowerPoint, 
            or Google Docs. Download, customize with your church information, and you're ready to go!
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2">
              <i className="ri-customer-service-line"></i>
              <span>Contact Support</span>
            </button>
            <button className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2">
              <i className="ri-book-open-line"></i>
              <span>View Tutorials</span>
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
